import React, { useState, useEffect } from "react";
import { Col, Form, Row, Button } from 'react-bootstrap';
import locationData from "../../countries.json";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import * as yup from "yup";
import { useUpdateUserProfileMutation } from "../../app/features/apiSlice";
import { toastError, toastSuccess } from "../../helpers";

const ResidentialDetails = ({ data, isLoading, error, refetch }) => {
    const { t } = useTranslation();
    const [countries, setCountries] = useState([]);

    const initialValues = {
        address: data?.user?.orgUser?.address || "",
        province: data?.user?.orgUser?.province || "",
        city: data?.user?.orgUser?.city || "",
        postcode: data?.user?.orgUser?.postcode || "",
    };

    const schema = yup.object().shape({
        address: yup.string().required("residenceAddressRequired"),
        province: yup.string().required("residenceCountryRequired"),
        city: yup.string().required("residenceCityRequired"),
        postcode: yup.string().required("residencePostcodeRequired"),
    });

    const [updateUser, { isLoading: isUpdating }] = useUpdateUserProfileMutation();

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = {
                address: values.address,
                province: values.province,
                city: values.city,
                postcode: values.postcode,
            };

            const response = await updateUser(payload).unwrap();
            const message = response?.message ?? "Residential details updated successfully";
            toastSuccess(message);
            await refetch();
        } catch (err) {
            const errorMessage = err?.data?.message || err?.message || t("error.serverNotResponding");
            toastError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const countryData = locationData.map((country) => country.name);
        setCountries(countryData);
    }, []);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (error) {
        console.error("Data loading error:", error);
        return <div>Error loading residential details</div>;
    }

    return (
        <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            enableReinitialize={true}
        >
            {({
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                touched,
                errors,
                isSubmitting,
            }) => (
                <Form
                    noValidate
                    onSubmit={handleSubmit}
                    className="add-inner-form"
                >
                    <h6 className="text-danger bold">
                        You can request changes to this information by writing to
                        support@excelior.ch
                    </h6>

                    <Row>
                        <Form.Group
                            as={Col}
                            xs="12"
                            md="6"
                            className="mb-3"
                        >
                            <Form.Label>{t("form.address")} *</Form.Label>
                            <Form.Control
                                type="text"
                                name="address"
                                placeholder={t("form.address")}
                                value={values.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.address && !!errors.address}
                                size="sm"
                            />
                            <Form.Control.Feedback type="invalid">
                                {t(`error.${errors.address}`)}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                            as={Col}
                            xs="12"
                            md="6"
                            className="mb-3"
                        >
                            <Form.Label>{t("form.country")} *</Form.Label>
                            <Form.Select
                                name="province"
                                value={values.province}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.province && !!errors.province}
                                size="sm"
                            >
                                <option value="">
                                    {t("form.select")}
                                </option>

                                {countries.map((country, i) => (
                                    <option key={i} value={country}>
                                        {country}
                                    </option>
                                ))}
                            </Form.Select>

                            <Form.Control.Feedback type="invalid">
                                {t(`error.${errors.province}`)}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group
                            as={Col}
                            xs="12"
                            md="6"
                            className="mb-3"
                        >
                            <Form.Label>{t("form.city")} *</Form.Label>
                            <Form.Control
                                type="text"
                                name="city"
                                placeholder={t("form.city")}
                                value={values.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.city && !!errors.city}
                                size="sm"
                            />
                            <Form.Control.Feedback type="invalid">
                                {t(`error.${errors.city}`)}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                            as={Col}
                            xs="12"
                            md="6"
                            className="mb-3"
                        >
                            <Form.Label>{t("form.postcode")} *</Form.Label>
                            <Form.Control
                                type="text"
                                name="postcode"
                                placeholder={t("form.postcode")}
                                value={values.postcode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.postcode && !!errors.postcode}
                                size="sm"
                            />
                            <Form.Control.Feedback type="invalid">
                                {t(`error.${errors.postcode}`)}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Row>

                    <Button
                        variant="primary-outline"
                        type="submit"
                        disabled={isSubmitting || isUpdating}
                    >
                        {t("update")}
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

export default ResidentialDetails;